import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to include cookies on all requests
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // On mount, check if we have a valid session via httpOnly cookie
    // Also check localStorage for backwards compatibility during migration
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      setToken(savedToken);
    }
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Cookie/token invalid — clear state
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    return await fetchUser();
  };

  const setAuthState = (newToken, userData) => {
    // Keep localStorage as fallback during migration (some components read it directly)
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  const clearAuth = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    // Backend sets httpOnly cookie automatically; we also keep token in memory
    setAuthState(newToken, userData);
    return userData;
  };

  const register = async (email, username, displayName, password, captchaToken = null, promoCode = null, referralCode = null, referralSource = null, guestId = null) => {
    const payload = {
      email,
      username,
      display_name: displayName,
      password
    };
    
    if (captchaToken) {
      payload.cf_turnstile_response = captchaToken;
    }
    if (promoCode) {
      payload.promo_code = promoCode;
    }
    if (referralCode) {
      payload.referral_code = referralCode;
      payload.referral_source = referralSource || 'direct';
    }
    if (guestId) {
      payload.guest_id = guestId;
    }
    
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData, promo_applied, referral_applied } = response.data;
    // Backend sets httpOnly cookie automatically; we also keep token in memory
    setAuthState(newToken, userData);
    return { user: userData, promoApplied: promo_applied, referralApplied: referral_applied };
  };

  const logout = async () => {
    // Clear httpOnly cookie via backend
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Ignore errors — clear local state regardless
    }
    clearAuth();
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, register, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
